import React, { useCallback, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isMobile } from 'react-device-detect';

import picsioConfig from '../../../../../config';
import Logger from '../../services/Logger';
import withErrorBoundary from '../ErrorBoundary/withErrorBoundary';
import Icon from '../Icon';
import { setDownloadPresets } from '../../store/reducers/user';

import ConversionDialogShell from '../AssetsCopyDialog/ConversionDialogShell';
import useAssetConversionForm from '../AssetsCopyDialog/useAssetConversionForm';
import { downloadStrategy } from '../AssetsCopyDialog/conversionDialogStrategies';
import normalizeFormatMimeType from '../../helpers/normalizeFormatMimeType';
import localization from '../../shared/strings';
import { bytesToSize } from '../../shared/utils';
import DownloadDialogAdditional from './DownloadDialogAdditional';

const { isMainApp, isProofing } = picsioConfig;

// When a website / proofing admin enables "Download as original format only",
// the download dialog must hide all conversion controls and force the original
// format on submit. Flag is delivered to the frontend via websiteConfig, which
// is populated by the websites service (see `websiteConfig.downloadAsOriginalOnly`).
const isForceOriginalOnly = () => (
  typeof window !== 'undefined'
  && !isMainApp
  && !!window.websiteConfig?.downloadAsOriginalOnly
);

function runFlyingFilesAnimation(buttonEl) {
  if (!buttonEl) return;
  const { offsetLeft, offsetTop } = buttonEl;
  const el = document.createElement('div');
  el.classList.add('flyingFiles');
  const root = createRoot(el);
  root.render(<Icon name="files" />);
  el.style.left = `${offsetLeft}px`;
  el.style.top = `${offsetTop}px`;
  el.addEventListener('transitionend', el.remove, false);
  document.body.appendChild(el);

  const target = document.querySelector('.toolbarPreviewLeft .downloadList')
    || document.querySelector('.toolbarCatalogLeft .downloadList')
    || document.querySelector('.toolbarCatalogLeft .toolbarGroup .toolbarButton:last-child')
    || document.querySelector('.toolbarCatalogTop .downloadList')
    || document.querySelector('.toolbarSelectedAssetsTop .toolbarDownload')
    || document.querySelector('.toolbarCatalogProofing .toolbarCatalogTop #button-downloadList');

  if (target) {
    const {
      top, left, height, width,
    } = target.getBoundingClientRect();
    el.style.left = `${left + width * 0.5}px`;
    el.style.top = `${top + height}px`;
  }
}

/**
 * Download-with-conversion dialog — thin wrapper around the shared conversion shell.
 * Responsibilities that are download-specific:
 *   - Adds the "archive / watermark / organize by collections" extras panel.
 *   - Runs the flying-files animation on submit.
 *   - Leaves dialog lifecycle management to `renderDownloadDialog`
 *     (which closes via handleCloseDialog once the download flow decides).
 */
function DownloadDialog(props) {
  const {
    close,
    onDownload,
    shouldFormatBeDisabled,
    assets,
    countAssets,
    downloadPresets,
    subscriptionFeatures,
    setDownloadPresets: updateDownloadPresets,
  } = props;

  const refBtnOk = useRef(null);
  const forceOriginalOnly = isForceOriginalOnly();

  const sourceMimeType = useMemo(() => (
    assets.length === 1 ? normalizeFormatMimeType(assets[0]?.mimeType) : undefined
  ), [assets]);

  const copyPreviewAssetId = !forceOriginalOnly && assets.length === 1
    ? assets[0]._id
    : undefined;

  const handleSubmit = useCallback((payload) => {
    Logger.log('User', 'DownloadDialogSubmit', { assetsCount: assets.length });
    onDownload(payload);
    if ((isMainApp || isProofing) && !isMobile) {
      setTimeout(() => runFlyingFilesAnimation(refBtnOk.current), 100);
    }
  }, [assets.length, onDownload]);

  const handleClose = useCallback(() => {
    Logger.log('User', 'DownloadDialogCancel');
    close();
  }, [close]);

  const form = useAssetConversionForm({
    strategy: downloadStrategy,
    shouldFormatBeDisabled,
    assets,
    selectionCount: countAssets,
    copyPreviewAssetId,
    downloadPresets,
    updateDownloadPresets,
    subscriptionFeatures,
    onSubmit: handleSubmit,
    onClose: handleClose,
    forceOriginalOnly,
  });

  const isWatermarkingAllowed = subscriptionFeatures.watermarks;
  const displayWatermark = isWatermarkingAllowed || !isMainApp;
  const canDownloadWithoutWatermark = isMainApp
    && form.isAssetsWithWatermark
    && assets[0]?.permissions?.downloadWithoutWatermark
    && displayWatermark;
  const disablePdfFormat = form.isAssetsWithWatermark
    && form.selectedSettings.withoutWatermark === false;

  // Per Figma (PCS-10728): when the website owner forces "original only",
  // conversion controls are hidden but the dialog still surfaces a file-info
  // block plus the archive / organize toggles.
  let originalOnlyInfo = null;
  if (forceOriginalOnly) {
    const isBulk = countAssets > 1;
    const totalAssetsSize = assets.reduce(
      (acc, asset) => acc + (Number(asset.fileSize) || 0),
      0,
    );
    originalOnlyInfo = (
      <div className="downloadDialog__originalOnly">
        <Choose>
          <When condition={isBulk}>
            <div className="downloadDialog__infoRow">
              <span className="downloadDialog__infoLabel">
                {localization.DOWNLOADDIALOG.textNumberOfFiles}
              </span>
              <span className="downloadDialog__infoValue">{countAssets}</span>
            </div>
            <div className="downloadDialog__infoRow">
              <span className="downloadDialog__infoLabel">
                {localization.DOWNLOADDIALOG.textTotalSize}
              </span>
              <span className="downloadDialog__infoValue">
                {bytesToSize(totalAssetsSize)}
              </span>
            </div>
          </When>
          <Otherwise>
            <div className="downloadDialog__infoRow">
              <span className="downloadDialog__infoLabel">
                {localization.DOWNLOADDIALOG.textFilename}
              </span>
              <span
                className="downloadDialog__infoValue downloadDialog__infoValue--truncate"
                title={assets[0]?.name}
              >
                {assets[0]?.name}
              </span>
            </div>
            <div className="downloadDialog__infoRow">
              <span className="downloadDialog__infoLabel">
                {localization.DOWNLOADDIALOG.textSize}
              </span>
              <span className="downloadDialog__infoValue">
                {bytesToSize(totalAssetsSize)}
              </span>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }

  let extraPanel = null;
  if (form.showExtraPanel) {
    extraPanel = (
      <>
        {originalOnlyInfo}
        <DownloadDialogAdditional
          canDownloadWithoutWatermark={canDownloadWithoutWatermark}
          selectedSettings={form.selectedSettings}
          onChange={form.handleChangeSettings}
          countAssets={countAssets}
        />
      </>
    );
  }

  return (
    <ConversionDialogShell
      form={form}
      strategy={downloadStrategy}
      sourceMimeType={sourceMimeType}
      assets={assets}
      copyPreviewAssetId={copyPreviewAssetId}
      selectionCount={countAssets}
      shouldFormatBeDisabled={shouldFormatBeDisabled}
      disablePdfFormat={disablePdfFormat}
      extraPanel={extraPanel}
      primaryButtonRef={refBtnOk}
    />
  );
}

DownloadDialog.defaultProps = {
  countAssets: 0,
  shouldFormatBeDisabled: false,
  subscriptionFeatures: {},
  downloadPresets: [],
};

DownloadDialog.propTypes = {
  countAssets: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  assets: PropTypes.arrayOf(PropTypes.object).isRequired,
  close: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  shouldFormatBeDisabled: PropTypes.bool,
  subscriptionFeatures: PropTypes.objectOf(PropTypes.any),
  downloadPresets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  })),
  setDownloadPresets: PropTypes.func.isRequired,
};

const ConnectedDownloadDialog = connect(
  (state) => ({
    subscriptionFeatures: state.user.subscriptionFeatures,
    downloadPresets: Array.isArray(state.user.presets) ? state.user.presets : [],
  }),
  { setDownloadPresets },
)(DownloadDialog);

export default withErrorBoundary(ConnectedDownloadDialog, { className: 'errorBoundaryOverlay' });
